// src/pages/admin/AdminLeaguesPage.tsx - Read-only league register: every
// league on the site, flagged when stuck, linked through to view (no
// destructive controls — those live on the Cleanup page).
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AdminLeague, LeagueFlag } from '../../types';
import BackLink from '../../components/common/BackLink';

type ChipKey = 'all' | 'attention' | 'inflight' | 'setup' | 'completed';

const CHIPS: { key: ChipKey; label: string; predicate: (l: AdminLeague) => boolean }[] = [
  { key: 'all', label: 'All', predicate: () => true },
  { key: 'attention', label: 'Needs attention', predicate: (l) => l.flags.length > 0 },
  {
    key: 'inflight',
    label: 'In flight',
    // Written by exclusion on purpose: production still holds a legacy
    // 'draft' status row. An inclusion list (['drafting','active','playoffs'])
    // would silently drop it from "in flight" — exactly the class of bug
    // this whole page exists to surface.
    predicate: (l) => l.league_status !== 'setup' && l.league_status !== 'completed',
  },
  { key: 'setup', label: 'Setup', predicate: (l) => l.league_status === 'setup' },
  { key: 'completed', label: 'Completed', predicate: (l) => l.league_status === 'completed' },
];

function unscoredLabel(weeks: number[]): string {
  if (weeks.length === 0) return 'UNSCORED';
  if (weeks.length === 1) return `UNSCORED W${weeks[0]}`;
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  return `UNSCORED W${first}–W${last}`;
}

const FLAG_BADGE: Record<LeagueFlag, { className: string; label: (l: AdminLeague) => string }> = {
  stalled_setup: {
    className: 'border-warning-600 text-warning-700',
    label: () => 'STALLED',
  },
  // Numbered scale, not bare `danger`: the danger/success/warning scales in
  // tailwind.config.js have no DEFAULT key, so `text-danger` compiles to
  // nothing and the badge would render as a neutral gray chip — reading
  // calmer than STALLED, which is exactly backwards.
  no_schedule: {
    className: 'border-danger-600 text-danger-700',
    label: () => 'NO SCHEDULE',
  },
  unscored: {
    className: 'border-danger-600 text-danger-700',
    label: (l) => unscoredLabel(l.unscored_weeks),
  },
};

export default function AdminLeaguesPage() {
  const { user } = useAuth();

  const [leagues, setLeagues] = useState<AdminLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [activeChip, setActiveChip] = useState<ChipKey>('all');

  const refetchLeagues = useCallback(async () => setLeagues(await apiService.getAdminLeagues()), []);

  useEffect(() => {
    if (!user?.is_superuser) return;
    (async () => {
      setLoading(true);
      setPageError(null);
      try {
        await refetchLeagues();
      } catch (e: any) {
        setPageError(e.message || 'Failed to load leagues');
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.is_superuser, refetchLeagues]);

  if (!user?.is_superuser) {
    return <Navigate to="/app" replace />;
  }

  const chip = CHIPS.find((c) => c.key === activeChip) ?? CHIPS[0];
  const filtered = leagues.filter(chip.predicate);
  // Copy before sorting — never mutate state. Array.prototype.sort is stable
  // in modern JS, so leagues that tie on "has flags" keep the server's
  // newest-first order.
  const ordered = [...filtered].sort(
    (a, b) => Number(b.flags.length > 0) - Number(a.flags.length > 0)
  );

  return (
    <div>
      <BackLink to="/app/admin">Back to Admin Panel</BackLink>
      <div className="border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Leagues</h1>
        <p className="text-secondary-600 mt-1">
          Every league on the site, newest first. Flagged rows are surfaced for a look, not a fix —
          this page is read-only.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {CHIPS.map((c) => {
          const active = c.key === activeChip;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveChip(c.key)}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border ${
                active
                  ? 'bg-ink-900 border-ink-900 text-paper'
                  : 'border-ink-900 text-ink-900 hover:bg-secondary-100'
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner w-6 h-6 mr-3" />
          <span className="text-secondary-500 text-sm">Loading leagues…</span>
        </div>
      )}

      {!loading && pageError && <p className="text-sm text-danger-700 py-4">{pageError}</p>}

      {!loading && !pageError && (
        <>
          {ordered.length === 0 ? (
            <p className="py-12 text-center font-display uppercase tracking-wider text-secondary-400">
              No leagues.
            </p>
          ) : (
            <div className="overflow-x-auto bg-white border border-secondary-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-secondary-500 uppercase tracking-wide text-xs border-b-2 border-ink-900">
                    <th className="py-2.5 px-4 text-left font-semibold">Name</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Flags</th>
                    <th className="py-2.5 px-4 text-right font-semibold">Teams</th>
                    <th className="py-2.5 px-4 text-right font-semibold">Season</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Commissioner</th>
                    <th className="py-2.5 px-4 text-right font-semibold">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {ordered.map((l) => (
                    <tr key={l.id} className="text-ink-900">
                      <td className="py-3 px-4 font-medium" data-testid="league-name">
                        {l.name}
                      </td>
                      <td className="py-3 px-4 text-secondary-600 uppercase text-xs">
                        {l.league_status}
                      </td>
                      <td className="py-3 px-4">
                        {l.flags.length === 0 ? (
                          <span className="text-secondary-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {l.flags.map((flag) => {
                              const badge = FLAG_BADGE[flag];
                              return (
                                <span
                                  key={flag}
                                  className={`inline-block px-1.5 py-0.5 border text-xs font-semibold uppercase tracking-wider ${badge.className}`}
                                >
                                  {badge.label(l)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-medium tabular-nums">
                        {l.team_count} / {l.max_teams ?? '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-display tabular-nums">
                        {l.season_year}
                      </td>
                      <td className="py-3 px-4 text-secondary-600">{l.commissioner || '—'}</td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/app/leagues/${l.id}`}
                          aria-label={`View ${l.name}`}
                          className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
